import {
  faApple,
  faDiscord,
  faFacebook,
  faGithub,
  faGoogle,
  faMicrosoft,
} from '@fortawesome/free-brands-svg-icons';
import { faRightToBracket } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { ReactNode } from 'react';
import config from '../config';
import { IdentityCallbackMode } from '../types';

/**
 * A credential field the provider needs, beyond its client id
 */
export type IdentityProviderField = {
  /**
   * The request body field, e.g. clientSecret
   */
  name: string;
  label: string;
  hint?: string;

  /**
   * Whether the value is encrypted and never shown again
   */
  secret?: boolean;

  /**
   * Offer a file picker (for Apple's .p8 key), read in the browser
   */
  file?: string;

  /**
   * Whether the provider works without this field
   */
  optional?: boolean;
};

export type IdentityProviderInfo = {
  id: string;
  name: string;
  icon: IconDefinition;
  defaultCallbackMode: IdentityCallbackMode;

  /**
   * The callback modes this provider supports, if not both
   */
  callbackModes?: IdentityCallbackMode[];

  /**
   * What the provider calls its client id
   */
  clientIdLabel: string;

  /**
   * The other credentials it needs
   */
  fields: IdentityProviderField[];

  /**
   * Where the OAuth app is created
   */
  consoleUrl: string;
  consoleName: string;
  guide: string;

  /**
   * A short checklist for creating the OAuth app. The full guide has more
   */
  createSteps: ReactNode[];

  /**
   * What to do with the callback URL(s) at the provider, for each mode
   */
  callbackNotes: Record<IdentityCallbackMode, ReactNode>;

  /**
   * Why a callback mode is recommended, or when it isn't
   */
  callbackModeAdvice: Record<IdentityCallbackMode, ReactNode>;
  goingLiveSteps: ReactNode[];
};

/**
 * JSONPad's callback URL, for providers set up with the "jsonpad" callback
 * mode (and for test sign-ins)
 */
export const jsonpadCallbackUrl = `${config.api.url}/identities/oauth/callback`;

/**
 * The OAuth providers identities can sign in with, in the order they're shown
 */
export const identityProviders: IdentityProviderInfo[] = [
  {
    id: 'google',
    name: 'Google',
    icon: faGoogle as IconDefinition,
    defaultCallbackMode: 'app',
    clientIdLabel: 'Client ID',
    fields: [{ name: 'clientSecret', label: 'Client secret', secret: true }],
    consoleUrl: 'https://console.cloud.google.com/auth/clients',
    consoleName: 'Google Cloud console',
    guide: '/docs/identity-oauth-google',
    createSteps: [
      <>
        In the Google Cloud console, choose a project (or create one) for your
        app.
      </>,
      <>
        Open <strong>Google Auth Platform</strong>. If you haven't used it in
        this project yet, click <strong>Get started</strong>, enter your app's
        name and support email, choose <strong>External</strong> for the
        audience, and finish the form.
      </>,
      <>
        Go to <strong>Clients</strong> and click <strong>Create client</strong>.
        Choose <strong>Web application</strong> and give it a name.
      </>,
      <>
        Leave the console open: you'll add redirect URIs in the next step, then
        copy the client ID and secret.
      </>,
    ],
    callbackNotes: {
      app: (
        <>
          Add each of these under <strong>Authorized redirect URIs</strong>. You
          don't need <strong>Authorized JavaScript origins</strong>.
        </>
      ),
      jsonpad: (
        <>
          Add this under <strong>Authorized redirect URIs</strong>. You don't
          need <strong>Authorized JavaScript origins</strong>.
        </>
      ),
    },
    callbackModeAdvice: {
      app: (
        <>
          Recommended. Google sends people straight back to your app, so the
          consent screen can show your app's own domain and branding.
        </>
      ),
      jsonpad: (
        <>
          Google sends people to JSONPad, which passes them on to your app. You
          only register one URL with Google, but the consent screen may show
          JSONPad's domain instead of yours.
        </>
      ),
    },
    goingLiveSteps: [
      <>
        While your app's publishing status is <strong>Testing</strong>, only the
        test users listed under <strong>Audience</strong> can sign in. Click{' '}
        <strong>Publish app</strong> there when you're ready.
      </>,
      <>
        JSONPad only asks for the basic <code>openid</code>, <code>email</code>{' '}
        and <code>profile</code> scopes, so Google doesn't need to review your
        app. To show your logo on the consent screen, submit your branding for
        verification.
      </>,
      <>Add your production return page to the group's redirect URLs.</>,
    ],
  },
  {
    id: 'github',
    name: 'GitHub',
    icon: faGithub as IconDefinition,
    defaultCallbackMode: 'jsonpad',
    clientIdLabel: 'Client ID',
    fields: [{ name: 'clientSecret', label: 'Client secret', secret: true }],
    consoleUrl: 'https://github.com/settings/applications/new',
    consoleName: 'GitHub developer settings',
    guide: '/docs/identity-oauth-github',
    createSteps: [
      <>
        In GitHub, go to <strong>Settings</strong> →{' '}
        <strong>Developer settings</strong> → <strong>OAuth Apps</strong> and
        click <strong>New OAuth app</strong>. (To create it for an organization,
        use the organization's settings instead.)
      </>,
      <>
        Enter your app's name and its homepage URL. People see these when they
        sign in.
      </>,
      <>
        Paste the callback URL from the next step into{' '}
        <strong>Authorization callback URL</strong>, leave{' '}
        <strong>Enable Device Flow</strong> unticked, and click{' '}
        <strong>Register application</strong>.
      </>,
      <>
        Copy the <strong>Client ID</strong>, then click{' '}
        <strong>Generate a new client secret</strong> and copy the secret.
        GitHub only shows it once.
      </>,
    ],
    callbackNotes: {
      app: (
        <>
          A GitHub OAuth app has only one{' '}
          <strong>Authorization callback URL</strong>. Every return page must be
          on the same host as it, at the same path or below it, so this mode
          rarely works for more than one environment.
        </>
      ),
      jsonpad: (
        <>
          Paste this into <strong>Authorization callback URL</strong>. It works
          for all of your return pages, including localhost.
        </>
      ),
    },
    callbackModeAdvice: {
      app: (
        <>
          GitHub sends people straight back to your app. GitHub only allows one
          callback URL per OAuth app, so this is awkward with more than one
          environment.
        </>
      ),
      jsonpad: (
        <>
          Recommended. GitHub sends people to JSONPad, which passes them on to
          whichever of your return pages started the sign-in.
        </>
      ),
    },
    goingLiveSteps: [
      <>
        Nothing to publish: anyone with a GitHub account can sign in as soon as
        the OAuth app exists.
      </>,
      <>
        Consider separate OAuth apps (and identity groups) for development and
        production, so a development secret can't be used in production.
      </>,
      <>Add your production return page to the group's redirect URLs.</>,
    ],
  },
  {
    id: 'apple',
    name: 'Apple',
    icon: faApple as IconDefinition,
    defaultCallbackMode: 'jsonpad',
    callbackModes: ['jsonpad'],
    clientIdLabel: 'Services ID',
    fields: [
      {
        name: 'teamId',
        label: 'Team ID',
        hint: 'Top right of the Apple Developer site, e.g. A1B2C3D4E5',
      },
      {
        name: 'keyId',
        label: 'Key ID',
        hint: "The key's ID in Certificates, Identifiers & Profiles",
      },
      {
        name: 'privateKey',
        label: 'Private key (.p8)',
        secret: true,
        file: '.p8,application/x-pkcs8,text/plain',
        hint: 'The file you downloaded from Apple. It can only be downloaded once.',
      },
    ],
    consoleUrl:
      'https://developer.apple.com/account/resources/identifiers/list/serviceId',
    consoleName: 'Apple Developer site',
    guide: '/docs/identity-oauth-apple',
    createSteps: [
      <>
        You need a paid <strong>Apple Developer Program</strong> membership ($99
        a year). Sign in to the Apple Developer site.
      </>,
      <>
        In <strong>Certificates, Identifiers &amp; Profiles</strong> →{' '}
        <strong>Identifiers</strong>, create an <strong>App ID</strong> with{' '}
        <strong>Sign in with Apple</strong> enabled, then a{' '}
        <strong>Services ID</strong> (this is your client ID) and enable{' '}
        <strong>Sign in with Apple</strong> on it too.
      </>,
      <>
        Configure the Services ID: pick the App ID as its primary, add your
        app's domain, and add the callback URL from the next step as a{' '}
        <strong>Return URL</strong>.
      </>,
      <>
        Under <strong>Keys</strong>, create a key with{' '}
        <strong>Sign in with Apple</strong> enabled and download the{' '}
        <code>.p8</code> file. Apple only lets you download it once. Note the{' '}
        <strong>Key ID</strong>, and your <strong>Team ID</strong> from the top
        right of the page.
      </>,
    ],
    callbackNotes: {
      app: (
        <>
          Apple posts its result as a form, which a page in your app can't read,
          so sign-ins have to come back through JSONPad.
        </>
      ),
      jsonpad: (
        <>
          Add this as a <strong>Return URL</strong> on your Services ID, under{' '}
          <strong>Sign in with Apple</strong>.
        </>
      ),
    },
    callbackModeAdvice: {
      app: <>Apple doesn't support this mode: its callback is a form post.</>,
      jsonpad: (
        <>
          Required. Apple posts the result to JSONPad, which passes it on to
          your app's return page as an ordinary request.
        </>
      ),
    },
    goingLiveSteps: [
      <>
        Apple needs every domain in your Return URLs to be verified, and won't
        accept <code>localhost</code>. Use JSONPad's callback URL (already
        verified) and test with a real domain, or a tunnel such as ngrok.
      </>,
      <>
        People can hide their email address, in which case Apple gives you a
        private relay address that forwards to them. To email those addresses,
        register your sending domain under{' '}
        <strong>Sign in with Apple for Email Communication</strong>.
      </>,
      <>
        Apple only sends someone's name the first time they agree to share it,
        so JSONPad stores it then. Ask for a display name in your app if you
        need one later.
      </>,
      <>Add your production return page to the group's redirect URLs.</>,
    ],
  },
  {
    id: 'microsoft',
    name: 'Microsoft',
    icon: faMicrosoft as IconDefinition,
    defaultCallbackMode: 'app',
    clientIdLabel: 'Application (client) ID',
    fields: [
      { name: 'clientSecret', label: 'Client secret', secret: true },
      {
        name: 'tenant',
        label: 'Tenant',
        optional: true,
        hint: 'Leave empty for "common": personal, work and school accounts. Use a tenant ID or domain to allow only one organization.',
      },
    ],
    consoleUrl:
      'https://entra.microsoft.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade',
    consoleName: 'Microsoft Entra admin center',
    guide: '/docs/identity-oauth-microsoft',
    createSteps: [
      <>
        In the Microsoft Entra admin center, go to{' '}
        <strong>App registrations</strong> and click{' '}
        <strong>New registration</strong>.
      </>,
      <>
        Name the app, and under <strong>Supported account types</strong> choose
        who can sign in — usually{' '}
        <strong>
          Accounts in any organizational directory and personal Microsoft
          accounts
        </strong>
        .
      </>,
      <>
        Add a <strong>Web</strong> redirect URI, using the URL from the next
        step, and register the app.
      </>,
      <>
        Copy the <strong>Application (client) ID</strong> from the overview,
        then go to <strong>Certificates &amp; secrets</strong>, add a{' '}
        <strong>New client secret</strong> and copy its <strong>Value</strong>{' '}
        (not the secret ID). Note when it expires.
      </>,
    ],
    callbackNotes: {
      app: (
        <>
          Add each of these as a <strong>Web</strong> redirect URI on the app
          registration. Microsoft allows <code>http://localhost</code> for
          development.
        </>
      ),
      jsonpad: (
        <>
          Add this as a <strong>Web</strong> redirect URI on the app
          registration.
        </>
      ),
    },
    callbackModeAdvice: {
      app: (
        <>
          Recommended. Microsoft sends people straight back to your app, and
          allows several redirect URIs, including localhost.
        </>
      ),
      jsonpad: (
        <>
          Microsoft sends people to JSONPad, which passes them on to your app.
          One URL to register, but the sign-in screen shows JSONPad's domain in
          the address bar.
        </>
      ),
    },
    goingLiveSteps: [
      <>
        <strong>Client secrets expire</strong>, after at most two years and by
        default after six months. Put a reminder in your calendar: when it
        expires, sign-ins stop until you add a new secret here.
      </>,
      <>
        JSONPad never trusts a Microsoft email address, because an administrator
        can set any address on an account in their own tenant. Identities
        created this way have no email address until they add one.
      </>,
      <>Add your production return page to the group's redirect URLs.</>,
    ],
  },
  {
    id: 'discord',
    name: 'Discord',
    icon: faDiscord as IconDefinition,
    defaultCallbackMode: 'app',
    clientIdLabel: 'Client ID',
    fields: [{ name: 'clientSecret', label: 'Client secret', secret: true }],
    consoleUrl: 'https://discord.com/developers/applications',
    consoleName: 'Discord developer portal',
    guide: '/docs/identity-oauth-discord',
    createSteps: [
      <>
        In the Discord developer portal, click <strong>New Application</strong>{' '}
        and give it a name. This name and its icon are what people see when they
        sign in.
      </>,
      <>
        Open <strong>OAuth2</strong> in the sidebar and add the redirect URL
        from the next step under <strong>Redirects</strong>.
      </>,
      <>
        Copy the <strong>Client ID</strong>, then <strong>Reset Secret</strong>{' '}
        to get a client secret, and copy it.
      </>,
    ],
    callbackNotes: {
      app: (
        <>
          Add each of these under <strong>Redirects</strong> on the OAuth2 page.
          Discord allows several, including <code>http://localhost</code> URLs.
        </>
      ),
      jsonpad: (
        <>
          Add this under <strong>Redirects</strong> on the OAuth2 page.
        </>
      ),
    },
    callbackModeAdvice: {
      app: (
        <>
          Recommended. Discord sends people straight back to your app, and
          allows as many redirect URLs as you need.
        </>
      ),
      jsonpad: (
        <>Discord sends people to JSONPad, which passes them on to your app.</>
      ),
    },
    goingLiveSteps: [
      <>
        Discord apps work for everyone as soon as they exist: there's nothing to
        publish.
      </>,
      <>
        An email address only counts as verified once the person has confirmed
        it with Discord; unconfirmed accounts sign in without one.
      </>,
      <>Add your production return page to the group's redirect URLs.</>,
    ],
  },
  {
    id: 'facebook',
    name: 'Facebook',
    icon: faFacebook as IconDefinition,
    defaultCallbackMode: 'app',
    clientIdLabel: 'App ID',
    fields: [{ name: 'clientSecret', label: 'App secret', secret: true }],
    consoleUrl: 'https://developers.facebook.com/apps',
    consoleName: 'Meta for Developers',
    guide: '/docs/identity-oauth-facebook',
    createSteps: [
      <>
        In Meta for Developers, create an app and choose the{' '}
        <strong>
          Authenticate and request data from users with Facebook Login
        </strong>{' '}
        use case.
      </>,
      <>
        Add the <strong>Facebook Login</strong> product, then open its{' '}
        <strong>Settings</strong> and add the redirect URL from the next step
        under <strong>Valid OAuth Redirect URIs</strong>.
      </>,
      <>
        Under <strong>App settings → Basic</strong>, copy the{' '}
        <strong>App ID</strong> and <strong>App secret</strong>.
      </>,
    ],
    callbackNotes: {
      app: (
        <>
          Add each of these under <strong>Valid OAuth Redirect URIs</strong>.
          Facebook requires <code>https</code>, so use its localhost exception
          or a tunnel for development.
        </>
      ),
      jsonpad: (
        <>
          Add this under <strong>Valid OAuth Redirect URIs</strong>. Your own
          pages then only need to be redirect URLs on the identity group, so
          localhost works.
        </>
      ),
    },
    callbackModeAdvice: {
      app: (
        <>
          Facebook sends people straight back to your app. It only accepts{' '}
          <code>https</code> URLs, apart from its own localhost exception.
        </>
      ),
      jsonpad: (
        <>
          Easier for development: Facebook sends people to JSONPad, which passes
          them on to your app, including localhost pages.
        </>
      ),
    },
    goingLiveSteps: [
      <>
        Switch the app from <strong>Development</strong> to{' '}
        <strong>Live</strong>. Until you do, only people with a role on the app
        can sign in.
      </>,
      <>
        Going live needs a privacy policy URL and a data deletion callback or
        instructions URL, under <strong>App settings → Basic</strong>.
      </>,
      <>
        JSONPad never trusts a Facebook email address, because Facebook doesn't
        say whether it has checked it.
      </>,
      <>Add your production return page to the group's redirect URLs.</>,
    ],
  },
];

export function getIdentityProvider(id: string): IdentityProviderInfo | null {
  return identityProviders.find(provider => provider.id === id) ?? null;
}

export function identityProviderName(id: string): string {
  return getIdentityProvider(id)?.name ?? id;
}

export function identityProviderIcon(id: string): IconDefinition {
  return getIdentityProvider(id)?.icon ?? faRightToBracket;
}

/**
 * Check a redirect URL the way the API does: an absolute https URL (http is
 * allowed for localhost), with no credentials or fragment. Returns an error
 * message, or null if it's fine
 */
export function redirectUrlError(value: string): string | null {
  let url: URL;
  try {
    url = new URL(value.trim());
  } catch {
    return 'Enter a full URL, e.g. https://myapp.com/auth/callback';
  }

  const local = ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);

  if (url.protocol !== 'https:' && !(url.protocol === 'http:' && local)) {
    return 'Use https (http is only allowed for localhost)';
  }

  if (url.username || url.password) {
    return "Redirect URLs can't contain a username or password";
  }

  if (value.includes('#')) {
    return "Redirect URLs can't contain a fragment (#)";
  }

  return null;
}

/**
 * Normalise a redirect URL the way the API does, so it can be compared with
 * the group's stored redirect URLs
 */
export function normaliseRedirectUrl(value: string): string {
  try {
    return new URL(value.trim()).toString();
  } catch {
    return value.trim();
  }
}

const TEST_VERIFIER_KEY_PREFIX = 'jsonpad-oauth-test:';

export function testVerifierKey(state: string): string {
  return `${TEST_VERIFIER_KEY_PREFIX}${state}`;
}

/**
 * Make a random client verifier for a test sign-in, and its SHA-256 hash (as
 * hex) for sending to the API
 */
export async function createClientVerifier(): Promise<[string, string]> {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const verifier = btoa(String.fromCharCode(...Array.from(bytes)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier)
  );
  const hash = Array.from(new Uint8Array(digest))
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');

  return [verifier, hash];
}

/**
 * Start a test sign-in from the dashboard: keep the client verifier for the
 * test page, then go to the provider
 */
export async function startTestSignIn(
  groupId: string,
  provider: string,
  start: (
    groupId: string,
    provider: string,
    clientVerifierHash: string
  ) => Promise<{ url: string } | void>
): Promise<void> {
  const [verifier, hash] = await createClientVerifier();
  const response = await start(groupId, provider, hash);

  if (!response) {
    return;
  }

  const state = new URL(response.url).searchParams.get('state');

  if (state) {
    try {
      sessionStorage.setItem(testVerifierKey(state), verifier);
    } catch {}
  }

  window.location.assign(response.url);
}
